// quality-gate-exempt: pre-modularization
import UpgradeModal from '@components/ui/UpgradeModal';
import { useCurrentLeagueKey } from '@hooks/useCurrentLeagueKey';
import { useServiceHealth } from '@hooks/useServiceHealth';
import { useTierAdjustments } from '@hooks/useTierAdjustments';
import { hasPlayerProps, type League, leagueKeyToLeague } from '@parlay-picker/shared/league';
import { NCAAB_MARKET_KEYS } from '@parlay-picker/shared/marketKey';
import { describeConfigAdjustments } from '@parlay-picker/shared/tierInputPolicy';
import type { AnalysisConfig } from '@views/picks/constants';
import { DEFAULT_ANALYSIS_CONFIG } from '@views/picks/constants';
import { X } from 'lucide-react';
import { type ReactElement, useMemo } from 'react';

import { AnalysisModeSelector } from './analysis-config/AnalysisModeSelector';
import { AnalysisRulesSections } from './analysis-config/AnalysisRulesSections';
import { BettingHistorySection } from './analysis-config/BettingHistorySection';
import { PROP_TYPE_OPTIONS } from './analysis-config/configConstants';
import type { AnalysisConfigPanelProps } from './analysis-config/configTypes';
import { DataSourcesSections } from './analysis-config/DataSourcesSections';
import { FactorLimitBanner } from './analysis-config/FactorLimitBanner';
import { LeagueSelector } from './analysis-config/LeagueSelector';
import { MLConfigSection } from './analysis-config/MLConfigSection';
import { ParlayIntelligenceSection } from './analysis-config/ParlayIntelligenceSection';
import { ParlayStrategySection } from './analysis-config/ParlayStrategySection';
import { PresetSelector } from './analysis-config/PresetSelector';
import { PropTypesSection } from './analysis-config/PropTypesSection';
import { Section } from './analysis-config/Section';
import { SportsbookSelector } from './analysis-config/SportsbookSelector';
import { Toggle } from './analysis-config/Toggle';
import { useAnalysisConfigPanel } from './analysis-config/useAnalysisConfigPanel';

/**
 * AnalysisConfigPanel - Slide-out panel for configuring AI analysis factors
 * Allows users to toggle which stats/factors influence pick generation
 */
export default function AnalysisConfigPanel({
  isOpen,
  onClose,
  config,
  setConfig,
  onSave,
  hasUnsavedChanges,
  settledLegCount = 0,
}: AnalysisConfigPanelProps): ReactElement | null {
  const serviceHealth = useServiceHealth(isOpen);
  const tierAdjustments = useTierAdjustments(isOpen);

  // Determine current league for section visibility
  const preferredLeagueKey = useCurrentLeagueKey();
  const currentLeague: League = leagueKeyToLeague(preferredLeagueKey);
  const leagueHasPlayerProps = hasPlayerProps(preferredLeagueKey);

  // Filter prop type options by league
  const leaguePropTypeOptions = useMemo(
    () =>
      !leagueHasPlayerProps
        ? PROP_TYPE_OPTIONS.filter((opt) => NCAAB_MARKET_KEYS.has(opt.key))
        : PROP_TYPE_OPTIONS,
    [leagueHasPlayerProps],
  );

  // Merge user config with defaults so every toggle reads from a fully populated object
  const cfg = useMemo(() => {
    const d = DEFAULT_ANALYSIS_CONFIG;
    return {
      banList: { ...d.banList, ...config.banList },
      propTypes: { ...d.propTypes, ...config.propTypes },
      playerStats: { ...d.playerStats, ...config.playerStats },
      advancedStats: { ...d.advancedStats, ...config.advancedStats },
      matchupData: { ...d.matchupData, ...config.matchupData },
      opponentDefense: { ...d.opponentDefense, ...config.opponentDefense },
      gameScript: { ...d.gameScript, ...config.gameScript },
      otherFactors: { ...d.otherFactors, ...config.otherFactors },
      efficiencyMetrics: { ...d.efficiencyMetrics, ...config.efficiencyMetrics },
      parlayStrategy: { ...d.parlayStrategy, ...config.parlayStrategy },
      ml: { ...d.ml, ...config.ml },
      parlayIntelligence: { ...d.parlayIntelligence, ...config.parlayIntelligence },
    };
  }, [config]);

  const {
    tier,
    upgradeFeature,
    setUpgradeFeature,
    expandedSections,
    toggleSection,
    toggleAllSections,
    factorLimit,
    enabledFactors,
    atFactorLimit,
    overFactorLimit,
    factorLimitProps,
    composedToggleProps,
    isLastStatWindow,
    updateConfig,
    resetToSaved,
    handleResetFactorsToDefaults,
    getEnabledCount,
    getTotalCount,
    toggleAllInSection,
    toggleCategory,
    toggleAllTrends,
  } = useAnalysisConfigPanel({ isOpen, onClose, config, setConfig });

  if (!isOpen) return null;

  // What the server had to change in the stored config to fit the plan, until the user saves.
  const tierAdjustmentMessage = describeConfigAdjustments(tierAdjustments.adjustments, tier);
  const statsApiDown = serviceHealth?.statsApi === false;
  const msfDown = serviceHealth?.msf === false;
  const allExpanded = Object.values(expandedSections).every((v) => v);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />

      {/* Panel */}
      <div
        data-testid="analysis-config-panel"
        className="fixed left-0 top-0 h-full w-full sm:w-80 bg-theme-surface border-r border-theme-border z-50 shadow-2xl flex flex-col animate-in slide-in-from-left duration-200"
      >
        {/* Header */}
        <div className="shrink-0 bg-theme-surface border-b border-theme-border p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-white">Analysis Factors</h3>
            <button
              type="button"
              data-testid="analysis-config-close-button"
              onClick={onClose}
              aria-label="Close analysis config"
              className="p-1 rounded hover:bg-theme-surface-hover text-theme-text-secondary hover:text-white transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs text-theme-text-tertiary">
              Toggle factors the AI considers when generating picks.
            </p>
            <button
              type="button"
              onClick={toggleAllSections}
              className="text-xs text-theme-primary hover:text-blue-300 transition whitespace-nowrap ml-2"
            >
              {allExpanded ? 'Collapse all' : 'Expand all'}
            </button>
          </div>

          {/* Factor limit warning — shown at top so free users see it immediately */}
          {factorLimit !== Infinity && (
            <FactorLimitBanner
              factorLimit={factorLimit}
              enabledFactors={enabledFactors}
              atFactorLimit={atFactorLimit}
              overFactorLimit={overFactorLimit}
              tier={tier}
              onReset={handleResetFactorsToDefaults}
            />
          )}

          {tierAdjustmentMessage && (
            <div
              data-testid="tier-adjustment-banner"
              className="mb-3 rounded-lg border border-theme-primary/30 bg-theme-primary/10 px-3 py-2 text-xs text-theme-text-secondary"
            >
              <p>{tierAdjustmentMessage}</p>
              <button
                type="button"
                onClick={async () => {
                  if (await onSave()) tierAdjustments.clear();
                }}
                className="mt-1.5 text-theme-primary hover:underline font-medium"
              >
                Keep these settings
              </button>
            </div>
          )}

          <LeagueSelector currentLeague={currentLeague} />
          <SportsbookSelector leagueKey={preferredLeagueKey} />

          <PresetSelector />

          <AnalysisModeSelector
            config={config}
            setConfig={setConfig}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            onUpgrade={setUpgradeFeature}
          />

          {/* Ban list — only for leagues with player props */}
          {leagueHasPlayerProps && (
            <Section
              title="Ban List"
              sectionKey="banList"
              isExpanded={expandedSections.banList}
              onToggleExpand={toggleSection}
              enabledCount={getEnabledCount('banList')}
              totalCount={getTotalCount('banList')}
            >
              <Toggle
                label="Use Banned Players List"
                description="Exclude players from your ban list in recommendations"
                checked={cfg.banList.includeBannedPlayers}
                onChange={(v) => updateConfig('banList', 'includeBannedPlayers', v)}
              />
            </Section>
          )}

          <ParlayStrategySection
            cfg={cfg as AnalysisConfig}
            isExpanded={expandedSections.parlayStrategy}
            onToggleExpand={toggleSection}
            setConfig={setConfig}
            updateConfig={updateConfig}
            tier={tier}
          />

          <MLConfigSection
            cfg={cfg as AnalysisConfig}
            isExpanded={expandedSections.ml ?? false}
            onToggleExpand={toggleSection}
            setConfig={setConfig}
          />

          <ParlayIntelligenceSection
            cfg={cfg as AnalysisConfig}
            isExpanded={expandedSections.parlayIntelligence ?? false}
            onToggleExpand={toggleSection}
            setConfig={setConfig}
          />

          <PropTypesSection
            config={config}
            isExpanded={expandedSections.propTypes}
            onToggleExpand={toggleSection}
            getEnabledCount={getEnabledCount}
            getTotalCount={getTotalCount}
            toggleAllInSection={toggleAllInSection}
            toggleCategory={toggleCategory}
            updateConfig={updateConfig}
            leaguePropTypeOptions={leaguePropTypeOptions}
          />

          <DataSourcesSections
            cfg={cfg as AnalysisConfig}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            getEnabledCount={getEnabledCount}
            getTotalCount={getTotalCount}
            toggleAllInSection={toggleAllInSection}
            updateConfig={updateConfig}
            factorLimitProps={factorLimitProps}
            leagueHasPlayerProps={leagueHasPlayerProps}
            statsApiDown={statsApiDown}
            msfDown={msfDown}
            isLastStatWindow={isLastStatWindow}
            tier={tier}
          />

          <AnalysisRulesSections
            cfg={cfg as AnalysisConfig}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            getEnabledCount={getEnabledCount}
            getTotalCount={getTotalCount}
            toggleAllInSection={toggleAllInSection}
            toggleAllTrends={toggleAllTrends}
            updateConfig={updateConfig}
            factorLimitProps={factorLimitProps}
            tier={tier}
          />

          <BettingHistorySection
            cfg={cfg as AnalysisConfig}
            config={config}
            isExpanded={expandedSections.bettingHistory}
            onToggleExpand={toggleSection}
            updateConfig={updateConfig}
            composedToggleProps={composedToggleProps}
            settledLegCount={settledLegCount}
            onUpgrade={setUpgradeFeature}
          />
        </div>

        {/* Sticky Footer */}
        <div className="shrink-0 bg-theme-surface border-t border-theme-border p-4">
          <div className="flex gap-2">
            <button
              data-testid="config-reset-button"
              type="button"
              onClick={resetToSaved}
              disabled={!hasUnsavedChanges}
              className={`flex-1 py-2 px-4 rounded-lg text-sm transition ${
                hasUnsavedChanges
                  ? 'bg-theme-surface-hover hover:bg-theme-border text-theme-text-secondary hover:text-theme-text'
                  : 'bg-theme-surface-hover text-theme-text-tertiary cursor-not-allowed'
              }`}
            >
              Revert
            </button>
            <button
              data-testid="config-save-button"
              type="button"
              onClick={async () => {
                if (await onSave()) tierAdjustments.clear();
              }}
              disabled={!hasUnsavedChanges}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition ${
                hasUnsavedChanges
                  ? 'bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill'
                  : 'bg-theme-surface-hover text-theme-text-tertiary cursor-not-allowed'
              }`}
            >
              {hasUnsavedChanges ? 'Save' : 'Saved'}
            </button>
          </div>
          <p className="text-xs text-gray-600 mt-2 text-center">
            {hasUnsavedChanges ? 'You have unsaved changes' : 'Settings persist for this session'}
          </p>
        </div>
      </div>

      {upgradeFeature && (
        <UpgradeModal
          mode="feature"
          feature={upgradeFeature}
          onDismiss={() => setUpgradeFeature(null)}
        />
      )}
    </>
  );
}
